import {
    isValidElement,
    Children,
    useMemo,
    type FC,
    useId
} from "react";
import {
    View
} from "react-native";
import type ISidebarGroupProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useSidebarRootContext,
    SidebarGroupContext
} from "../../context";
import type {
    SidebarGroupContextType
} from "../../type";
import {
    getSidebarMeasures
} from "../../stylesheet";
import SidebarGroupAction from "../groupAction";
import SidebarGroupLabel from "../groupLabel";

const SidebarGroup: FC<ISidebarGroupProps> = ({
    customTheme: customThemeProp,
    children,
    style,
    title,
    ...props
}) => {
    const root = useSidebarRootContext({
        part: "Sidebar.Group"
    });

    const customTheme = customThemeProp ?? root?.customTheme;

    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const labelId = useId();

    const items = Children.toArray(children);

    const hasLabel = !!title || items.some((item) => isValidElement(item) && item.type === SidebarGroupLabel);

    const actions = title ? items.filter((item) => isValidElement(item) && item.type === SidebarGroupAction) : [];

    const content = items.filter((item) => !actions.includes(item));

    const measures = getSidebarMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        spaces
    });

    const {
        container: containerDynamicStyle
    } = useStyles({
        measures,
        spaces
    });

    const context = useMemo<SidebarGroupContextType>(() => ({
        labelId
    }), [
        labelId
    ]);

    const renderLabel = () => {
        if(!title) {
            return null;
        }

        return <SidebarGroupLabel
            customTheme={customTheme}
        >
            {title}
            {actions}
        </SidebarGroupLabel>;
    };

    return <SidebarGroupContext.Provider
        value={context}
    >
        <View
            {...props}
            aria-labelledby={hasLabel ? labelId : undefined}
            role={hasLabel ? "group" : undefined}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
        >
            {renderLabel()}
            {content}
        </View>
    </SidebarGroupContext.Provider>;
};
export default SidebarGroup;
